import { useEffect } from 'react'
import { ApprovalHistoryEntry, WaitingApproval } from '@/types/approvals'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import { getList } from '../request-wrappers'

const MY_APPROVALS_URL = '/v2/pd-approval/my-approvals'

type MyApprovalsListOptions<T> = UseGetListOptions<T> & {
  projectId: string
}

export const APPROVALS_PAGE_SIZE = 20

export const WAITING_FILL_TARGET = APPROVALS_PAGE_SIZE

/**
 * Someone with hundreds of approved-but-open documents and nothing waiting
 * would otherwise pull the whole project before the empty state could show.
 * This prevents the UI from stalling when there are many pages of approvals.
 */
export const WAITING_FILL_MAX_PAGES = 5

/**
 * MARK: Action Hook: Get Waiting Approvals
 *
 * Because of how approvals work, we can't fetch a simple list of approvals waiting.
 * pd-approval has to filter down documents that have policies with you on it to ones
 * that aren't satisfied yet, so the list returned by the server may not include all,
 * so even though we request 20, we may only get back 15, so we keep requesting until
 * we fill up a single page.
 */
export function useGetWaitingApprovals({
  projectId,
  filters,
  enabled = true,
  ...options
}: MyApprovalsListOptions<WaitingApproval>) {
  const listFilters = {
    ...filters,
    limit: `${APPROVALS_PAGE_SIZE}`,
    project_id: projectId,
  }
  const query = useGetList<WaitingApproval>({
    filters: listFilters,
    queryKey: ['my-approvals', 'waiting', listFilters],
    serviceFn: (serviceFnOptions) =>
      getList<WaitingApproval>({
        url: `${MY_APPROVALS_URL}/waiting`,
        ...serviceFnOptions,
      }),
    enabled: enabled && !!projectId,
    ...options,
  })

  const pagesLoaded = query.data?.pages.length ?? 0
  // `fetchNextPage` ignores `enabled`, so the fill has to check it itself.
  const isFilling =
    enabled &&
    !!query.hasNextPage &&
    query.allData.length < WAITING_FILL_TARGET &&
    pagesLoaded < WAITING_FILL_MAX_PAGES

  // `pagesLoaded` is an intentional dep. A page that resolves quickly can start
  // and finish inside one render, so `isFetching` never visibly flips and the
  // effect wouldn't re-run. Re-running whenever a page lands keeps the fill going.
  const { isFetching, fetchNextPage } = query
  useEffect(() => {
    if (isFilling && !isFetching) fetchNextPage()
  }, [isFilling, isFetching, fetchNextPage, pagesLoaded])

  return {
    waitingApprovals: query.allData,
    isFilling,
    ...query,
  }
}

/**
 * MARK: Action Hook: Get Approval History
 * The signed-in user's own past approvals and rejections in one project, newest first.
 * Pass a `type` filter to narrow to approvals or rejections; omit it to get both.
 * Entries are the user's current decisions: pd-approval drops any action the same
 * user later reversed, so a document they rejected and then approved shows only
 * under approvals.
 */
export function useGetApprovalHistory({
  projectId,
  filters,
  enabled = true,
  ...options
}: MyApprovalsListOptions<ApprovalHistoryEntry>) {
  const listFilters = {
    limit: `${APPROVALS_PAGE_SIZE}`,
    ...filters,
    project_id: projectId,
  }
  const query = useGetList<ApprovalHistoryEntry>({
    filters: listFilters,
    queryKey: ['my-approvals', 'history', listFilters],
    serviceFn: (serviceFnOptions) =>
      getList<ApprovalHistoryEntry>({
        url: `${MY_APPROVALS_URL}/history`,
        ...serviceFnOptions,
      }),
    enabled: enabled && !!projectId,
    ...options,
  })

  return {
    approvalHistory: query.allData,
    ...query,
  }
}
